import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import SectionHeading from "@/components/base/SectionHeading";
import FaqAccordion from "@/components/feature/FaqAccordion";
import { faqs, recoverySteps, site } from "@/mocks/site";
import {
  emergencyNotes,
  helpChannels,
  legalPoints,
  psychologicalImpacts,
  traumaPhases,
} from "@/mocks/knowledge";

const knowledgeImage =
  "https://readdy.ai/api/search-image?query=conceptual%20editorial%20photograph%20of%20a%20single%20wooden%20chair%20beside%20a%20rain%20streaked%20window%2C%20warm%20morning%20light%20entering%20a%20dim%20quiet%20room%2C%20empty%20space%2C%20desaturated%20rust%20and%20cream%20tones%2C%20soft%20haze%2C%20fine%20film%20grain%2C%20hopeful%20calm%20mood%2C%20no%20people&width=1200&height=800&seq=knowledge-room-01&orientation=landscape";

export default function Knowledge() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "伤害与恢复｜网暴黑料的心理影响与求助指引",
    description:
      "系统梳理网暴黑料对当事人造成的心理影响、创伤阶段、恢复路径，以及平台投诉、证据保全、心理援助与法律维权等求助渠道。",
    inLanguage: "zh-CN",
    publisher: { "@type": "Organization", name: site.name },
  };

  return (
    <>
      <title>伤害与恢复｜网暴黑料心理影响与求助指引 - 回声墙</title>
      <meta
        name="description"
        content="网暴黑料会造成哪些心理影响？从急性应激、过度警觉到抑郁与社交回避，本文梳理创伤阶段、恢复路径，并提供平台投诉、证据保全、心理援助与法律维权的求助指引。"
      />
      <meta name="keywords" content="网暴黑料心理,网暴黑料伤害,网暴黑料后果,网暴黑料影响,网暴维权" />
      <link rel="canonical" href="/knowledge" />
      <meta property="og:type" content="article" />
      <meta property="og:title" content="伤害与恢复｜网暴黑料心理影响与求助指引 - 回声墙" />
      <meta
        property="og:description"
        content="从心理影响到创伤阶段，再到恢复路径与求助渠道，一份写给网暴黑料当事人的完整指引。"
      />
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>

      <section className="bg-secondary-950 pt-28 md:pt-36 pb-14 md:pb-20">
        <div className="max-w-editorial mx-auto px-4 md:px-8">
          <div className="[&_a]:text-background-200/70 [&_span]:text-background-100/80">
            <Breadcrumb items={[{ label: "伤害与恢复" }]} />
          </div>
          <h1 className="mt-6 font-heading text-3xl md:text-5xl font-black text-background-50 leading-tight max-w-4xl">
            伤害与恢复
          </h1>
          <p className="mt-5 max-w-3xl text-sm md:text-base leading-relaxed text-background-200/85">
            网暴黑料的伤害往往不在第一天爆发，而是在舆论平息之后慢慢显现。这一页把当事人常见的心理反应、创伤阶段与可操作的求助路径整理在一起，
            希望你在最混乱的时候，能有一个可以照着走的方向。
          </p>
        </div>
      </section>

      <section id="impacts" className="bg-background-50">
        <div className="max-w-editorial mx-auto px-4 md:px-8 py-14 md:py-20">
          <SectionHeading
            eyebrow="心理影响"
            title="当攻击停下来，身体和情绪还在继续"
            description="下列反应在回声墙的当事人回访中反复出现。它们不是「想太多」，而是长期处于被攻击状态后的真实结果。"
          />
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {psychologicalImpacts.map((item) => (
              <div
                key={item.title}
                className="rounded-lg border border-background-200 bg-background-100/60 p-5 md:p-6 hover:border-accent-300 transition-colors"
              >
                <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <h3 className="mt-5 font-heading text-base md:text-lg font-bold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-100 border-y border-background-200">
        <div className="max-w-editorial mx-auto px-4 md:px-8 py-14 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
            <div className="lg:col-span-5">
              <div className="rounded-lg overflow-hidden bg-background-200 h-64 md:h-[420px]">
                <img
                  src={knowledgeImage}
                  alt="雨后窗边的一把木椅与透入室内的晨光，象征网暴黑料当事人逐步走向恢复"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div className="lg:col-span-7">
              <SectionHeading
                eyebrow="创伤阶段"
                title="伤害有它自己的时间表"
                description="了解自己正处在哪个阶段，能帮助你判断「现在需要什么」，而不是责怪自己恢复得太慢。"
              />
              <ol className="mt-8 flex flex-col gap-6">
                {traumaPhases.map((phase, index) => (
                  <li key={phase.phase} className="relative pl-8">
                    <span className="absolute left-0 top-1.5 w-4 h-4 rounded-full bg-accent-500 border-2 border-background-100"></span>
                    {index < traumaPhases.length - 1 && (
                      <span className="absolute left-[7px] top-6 bottom-[-24px] w-px bg-background-300"></span>
                    )}
                    <div className="flex flex-wrap items-baseline gap-3">
                      <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950">
                        {phase.phase}
                      </h3>
                      <span className="text-xs text-foreground-500">{phase.time}</span>
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{phase.desc}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section id="recovery" className="bg-background-50">
        <div className="max-w-editorial mx-auto px-4 md:px-8 py-14 md:py-20">
          <SectionHeading
            eyebrow="恢复路径"
            title="五步，把生活的解释权收回来"
            description="顺序不是固定的，你可以从最有力量的一步开始。每一步都不需要做到完美，做到「比昨天多一点」就足够。"
          />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
            {recoverySteps.map((item) => (
              <div
                key={item.step}
                className="rounded-lg border-t-2 border-secondary-400 bg-background-100/60 p-5"
              >
                <span className="font-heading text-2xl font-black text-secondary-500">{item.step}</span>
                <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="help" className="bg-background-100 border-y border-background-200">
        <div className="max-w-editorial mx-auto px-4 md:px-8 py-14 md:py-20">
          <SectionHeading
            eyebrow="求助渠道"
            title="可以找谁，该按什么顺序找"
            description="很多当事人卡在「不知道该找谁」上。下面几条渠道可以同时进行，也可以只选择你现在做得动的那一条。"
          />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {helpChannels.map((channel) => (
              <div
                key={channel.title}
                className="flex items-start gap-4 rounded-lg bg-background-50 border border-background-200 p-5 md:p-6"
              >
                <span className="shrink-0 w-11 h-11 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className={`${channel.icon} text-xl`}></i>
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">{channel.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{channel.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div id="emergency" className="rounded-lg border border-primary-300 bg-primary-50 p-6 md:p-7">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-500 text-background-50">
                  <i className="ri-alarm-warning-line text-xl"></i>
                </span>
                <h3 className="font-heading text-lg font-bold text-primary-900">紧急情况请先处理安全</h3>
              </div>
              <ul className="mt-5 flex flex-col gap-3">
                {emergencyNotes.map((note) => (
                  <li key={note} className="flex items-start gap-3 text-sm leading-relaxed text-primary-900">
                    <i className="ri-arrow-right-s-fill text-primary-500 mt-0.5"></i>
                    <span>{note}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-7">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className="ri-scales-3-line text-xl"></i>
                </span>
                <h3 className="font-heading text-lg font-bold text-foreground-950">关于法律维权的基本认知</h3>
              </div>
              <ul className="mt-5 flex flex-col gap-3">
                {legalPoints.map((point) => (
                  <li key={point} className="flex items-start gap-3 text-sm leading-relaxed text-foreground-700">
                    <i className="ri-checkbox-circle-line text-secondary-500 mt-0.5"></i>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section id="faq" className="bg-background-50">
        <div className="max-w-editorial mx-auto px-4 md:px-8 py-14 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
            <div className="lg:col-span-4">
              <SectionHeading
                eyebrow="常见问题"
                title="当事人与围观者最关心的八个问题"
                description="如果你的问题不在这里，也欢迎直接写信给编辑部。"
              />
              <Link
                to="/about#help"
                className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer bg-secondary-500 text-background-50 hover:bg-secondary-600 transition-colors"
              >
                <i className="ri-mail-send-line"></i>
                写信给编辑部
              </Link>
            </div>
            <div className="lg:col-span-8">
              <FaqAccordion items={faqs} idPrefix="knowledge-faq" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}